import clsx from "clsx";
import { useAtomValue } from "jotai";
import React, { useContext, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Hide, Show } from "@/assets/icons/icons";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { useDebounce } from "@/hooks/useDebounce";
import { DataContext } from "@/provider/DataContextProvider";
import { checkCscLogin } from "@/services/api";
import {
  BasicCustomerDataTwoFactorMethod,
  CheckCscLoginResponseCheckCscLoginStatus,
} from "@/services/model";
import { FORGOT_PASSWORD_URL } from "@/utils/apiUrls";
import { accessTokenAtom } from "@/utils/atoms";
import { BACKEND_EMAIL_REGEX, DEFAULT_PASSWORD_REGEX } from "@/utils/constants";
import { createReferenceId } from "@/utils/initializeSentry";

type AuthFormFieldsProps = {
  isExistingAccount: boolean | null;
  setIsExistingAccount: React.Dispatch<React.SetStateAction<boolean | null>>;
  setTwoFactorMethod: React.Dispatch<
    React.SetStateAction<BasicCustomerDataTwoFactorMethod | null>
  >;
  hasApiError?: boolean;
  setHasApiError?: React.Dispatch<React.SetStateAction<boolean>>;
};

export type AuthFormFieldsType = {
  email: string;
  password: string;
  passwordRepeat: string;
};

export const AuthFormFields = ({
  isExistingAccount,
  setIsExistingAccount,
  setTwoFactorMethod,
  hasApiError = false,
  setHasApiError,
}: AuthFormFieldsProps) => {
  const { t } = useTranslation();
  const { setDataContext } = useContext(DataContext);
  // Register form with email using react-hook-form
  const {
    register,
    formState: { isValidating, errors, isSubmitting },
    watch,
    reset,
    resetField,
    trigger,
    clearErrors,
  } = useFormContext<AuthFormFieldsType>();

  const accessToken = useAtomValue(accessTokenAtom);

  // State for password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);

  const emailHasErrors = !!errors.email;
  const email = !isValidating && !emailHasErrors && watch("email");
  const [debouncedEmail, isDebouncing] = useDebounce(email, 500);

  useEffect(() => {
    if (emailHasErrors) setIsExistingAccount(null);
  }, [emailHasErrors, setIsExistingAccount]);

  // Reset password field when isExistingAccount changes
  useEffect(() => {
    if (isExistingAccount === true) {
      // Existing account: clear passwordRepeat from form state
      resetField("passwordRepeat");
      reset({ password: "" }, { keepValues: false });
      trigger();
    } else if (isExistingAccount === false) {
      // New account: reset both fields
      reset({ password: "", passwordRepeat: "" }, { keepValues: false });
      trigger();
    }
  }, [isExistingAccount, reset, resetField, trigger]);

  // Force refire when form submission starts (indicating a resubmission)
  useEffect(() => {
    if (isExistingAccount === false && setHasApiError) {
      setHasApiError(false);
    }
  }, [isExistingAccount, setHasApiError]);

  // Use debouncedEmail in useEffect to check if email is already in use
  useEffect(() => {
    if (debouncedEmail) {
      const referenceId = createReferenceId();

      // Username is the url encoded email value
      checkCscLogin(debouncedEmail, {
        headers: {
          "X-Reference-Id": referenceId,
        },
      }).then((res) => {
        if (
          res.checkCscLoginStatus ===
          CheckCscLoginResponseCheckCscLoginStatus.login_available
        ) {
          setIsExistingAccount(false);
          // Clear twoFactorMethod for new accounts
          setTwoFactorMethod(null);
        } else if (
          res.checkCscLoginStatus ===
          CheckCscLoginResponseCheckCscLoginStatus.login_exists
        ) {
          setIsExistingAccount(true);
          setDataContext((prev) => ({
            ...prev,
            isEmailVerified: res.emailVerified,
          }));

          // Only set twoFactorMethod for existing accounts
          if (
            res.method &&
            res.method !== BasicCustomerDataTwoFactorMethod.STANDARD
          ) {
            setTwoFactorMethod(res.method);
          }
        }
      });
    }
  }, [
    debouncedEmail,
    setIsExistingAccount,
    accessToken,
    setTwoFactorMethod,
    setDataContext,
  ]);

  return (
    <div className="grid max-w-[472px] gap-y-6">
      <FormField
        className={clsx(
          isDebouncing &&
            "[&>.relative>input]:animate-pulse [&>.relative>input]:bg-gray-50 [&>.relative>input]:opacity-50",
        )}
        id="new-email-input"
        label={t("user-account.personalDetails.loginData.email")}
        autocomplete="email"
        name="email"
        errors={errors}
        register={register}
        type="email"
        placeholder={t(
          "translation:Onboarding.sections.personal-details.form.placeholder.email",
        )}
        options={{
          required: t("translation:label.validation.required"),
          pattern: {
            value: BACKEND_EMAIL_REGEX,
            message: t("translation:label.validation.email"),
          },
          maxLength: 50,
          backendError: t(
            "user-account.personalDetails.loginData.emailInUseError",
          ),
          invalidEmailError: t(
            "user-account.personalDetails.loginData.emailInvalid",
          ),
        }}
      />
      <div>
        <FormField
          label={t("user-account.personalDetails.loginData.password")}
          id="password-input"
          autocomplete="current-password"
          name="password"
          errors={errors}
          register={register}
          type={showPassword ? "text" : "password"}
          placeholder={t(
            "translation:Onboarding.sections.personal-details.form.placeholder.password",
          )}
          options={{
            required: t("translation:label.validation.required"),
            ...(isExistingAccount === false && {
              pattern: {
                message: t("translation:label.validation.password"),
                value: DEFAULT_PASSWORD_REGEX,
              },
            }),
            backendError: t(
              "user-account.personalDetails.loginData.passwordError",
            ),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              if (errors.email) {
                clearErrors("email");
              }
            },
          }}
          icon={
            <button
              type="button"
              className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center p-1 focus:outline-none"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <Hide width={30} height={30} />
              ) : (
                <Show width={30} height={30} />
              )}
            </button>
          }
        />

        {(isExistingAccount || (isExistingAccount === null && hasApiError)) && (
          <a
            aria-label="Forgot password?"
            tabIndex={0}
            href={FORGOT_PASSWORD_URL.toString()}
            className="mt-2 block text-sm font-semibold text-primary-100 focus-within:underline hover:underline focus:underline focus-visible:underline active:underline"
            target="_blank"
            rel="noreferrer"
          >
            {t("user-account.personalDetails.loginData.forgotPassword")}
          </a>
        )}
      </div>
      {!isExistingAccount && !hasApiError && (
        <FormField
          label={t("user-account.personalDetails.loginData.passwordRepeat")}
          id="password-repeat-input"
          autocomplete="repeat-password"
          name="passwordRepeat"
          errors={errors}
          register={register}
          type={showPasswordRepeat ? "text" : "password"}
          placeholder={t(
            "translation:Onboarding.sections.personal-details.form.placeholder.password",
          )}
          options={{
            disabled: isExistingAccount === null,
            required: t("translation:label.validation.required"),
            validate: {
              match: (v) =>
                v === watch("password") ||
                t("user-account.personalDetails.loginData.passwordNoMatch"),
            },
            backendError: t(
              "user-account.personalDetails.loginData.passwordError",
            ),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              if (errors.passwordRepeat) {
                clearErrors("passwordRepeat");
              }
            },
          }}
          icon={
            <button
              type="button"
              tabIndex={-1}
              className={clsx(
                "absolute right-3 top-1/2 flex -translate-y-1/2 items-center p-1 focus:outline-none",
                isExistingAccount === null && "cursor-not-allowed opacity-20",
              )}
              onClick={() =>
                isExistingAccount !== null && setShowPasswordRepeat((v) => !v)
              }
              aria-label={
                showPasswordRepeat ? "Hide password" : "Show password"
              }
              disabled={isExistingAccount === null}
            >
              {showPasswordRepeat ? (
                <Hide width={30} height={30} />
              ) : (
                <Show width={30} height={30} />
              )}
            </button>
          }
        />
      )}
    </div>
  );
};
